import React, { useState } from "react";
import { DateTime } from "luxon";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../shadcn/components/ui/card";
import { Badge } from "../../../../shadcn/components/ui/badge";
import { Input } from "../../../../shadcn/components/ui/input";
import { Button } from "../../../../shadcn/components/ui/button";
import { ScrollArea } from "../../../../shadcn/components/ui/scroll-area";
import { cn } from "../../../../shadcn/lib/utils";

interface BroadcastMessagesProps {
  announcements: any[];
  onSendMessage: (message: string) => void;
  status: string;
}

export default function BroadcastMessages({
  announcements,
  onSendMessage,
  status,
}: BroadcastMessagesProps) {
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message.trim());
      setMessage("");
    }
  };

  const formatTime = (timestamp: string | number) => {
    if (typeof timestamp === "string") {
      return DateTime.fromISO(timestamp)
        .setZone("Asia/Kolkata")
        .toFormat("hh:mm:ss a");
    }
    return DateTime.fromMillis(timestamp)
      .setZone("Asia/Kolkata")
      .toFormat("hh:mm:ss a");
  };

  const getAnnouncementType = (type: string) => {
    switch (type) {
      case "time_extension":
        return "Time Extension";
      case "announcement":
        return "Announcement";
      case "system":
        return "System";
      default:
        return "System";
    }
  };

  const getAnnouncementTypeClass = (type: string) => {
    switch (type) {
      case "time_extension":
        return "bg-yellow-100 text-yellow-800";
      case "announcement":
        return "bg-blue-100 text-blue-800";
      case "system":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const maskEmailInAnnouncement = (message: string) => {
    return message.replace(/(\S+@\S+\.\S+)/g, (email) => {
      const domain = email.split("@")[1];
      const local = email.split("@")[0];
      return `${local[0]}***${local[local.length - 1]}@${domain}`;
    });
  };

  const sortedAnnouncements = [...announcements].sort((a, b) => {
    const timeA =
      typeof a.created_at === "string"
        ? DateTime.fromISO(a.created_at).toMillis()
        : a.created_at || 0;
    const timeB =
      typeof b.created_at === "string"
        ? DateTime.fromISO(b.created_at).toMillis()
        : b.created_at || 0;
    return timeB - timeA; // Newest first (descending order)
  });

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          Broadcast Messages
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Time extensions and announcements are auto-logged here in real time.
        </p>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <ScrollArea className="h-[200px] pr-2">
          <div className="space-y-1.5">
            {sortedAnnouncements.length > 0 ? (
              sortedAnnouncements.map((announcement, index) => {
                const type = getAnnouncementType(announcement.type);
                const typeClass = getAnnouncementTypeClass(announcement.type);
                const time = formatTime(announcement.created_at);
                const text = maskEmailInAnnouncement(
                  announcement.announcement || ""
                );

                return (
                  <div
                    key={announcement.uuid || index}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs"
                  >
                    <div className="flex justify-between items-center mb-0.5">
                      <span className="text-gray-500 text-[11px]">{time}</span>
                      <Badge
                        className={cn(
                          "text-[10px] px-1.5 py-0 uppercase",
                          typeClass
                        )}
                      >
                        {type}
                      </Badge>
                    </div>
                    <div className="text-gray-900 text-xs">{text}</div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-gray-500 text-sm">
                No broadcast messages yet.
              </div>
            )}
          </div>
        </ScrollArea>
        {status !== "end" && status !== "cancelled" && (
          <form onSubmit={handleSubmit} className="flex gap-1.5 mt-2">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type broadcast message to all bidders..."
              className="flex-1 rounded-full text-xs h-8 border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Button
              type="submit"
              className="rounded-full bg-orange-500 hover:bg-orange-600 h-8 px-4 text-xs"
            >
              Send
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
